import ComponentGuard from '@/components/ui/custom/ComponentGuard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import Checkbox from '@/components/ui/custom/dataInput/Checkbox';
import ChoiceSelector from '@/components/ui/custom/dataInput/ChoiceSelector';
import { unitsQueryKeys } from '@/data/services/querykeys';
import { isSuperAdmin } from '@/functions/auth';
import { useQueries } from '@tanstack/react-query';
import { compact } from 'lodash';
import React, { Fragment } from 'react';
import { Control, useController } from 'react-hook-form';
import { LessonFields } from './LessonBoxForm';
import { useParams } from 'react-router-dom';
import useAuth from '@/data/hook/useAuth';
import { useTranslation } from 'react-i18next';
import { UserTypeEnum } from '@/models/User';

type Props = {
  control: Control<LessonFields, any, LessonFields>;
};

export default function LessonManageBank({ control }: Props) {
  const {
    field: allowedUnitsIdsField,
    formState: { defaultValues },
  } = useController({ control, name: 'allowedUnitsIds' });
  const { field: inBankField } = useController({ control, name: 'inBank' });
  const { field: isPublicInBankField } = useController({
    control,
    name: 'isPublicInBank',
  });
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseBox.form',
  });
  const { slug } = useParams();
  const { user } = useAuth();

  const defaultAllowedUnitsIds = compact(defaultValues?.allowedUnitsIds) || [];
  const allowedUnitsIds = defaultAllowedUnitsIds || [];
  const inBank = inBankField.value;

  const unitsQueries = useQueries({
    queries: allowedUnitsIds.map(unitId => ({
      ...unitsQueryKeys.get(unitId),
      enabled: inBank,
    })),
  });
  const units = compact(unitsQueries.map(query => query.data)) || [];

  const isSuper = isSuperAdmin(user?.userType);

  const showBankInputs = () => {
    const isAuthor = user?.id === defaultValues?.author;
    return isAuthor || isSuper;
  };
  return (
    <Fragment>
      <ConditionalRenderer condition={showBankInputs()}>
        <Checkbox
          label={t('inBank')}
          className={{ input: 'border-2 border-primary rounded-md w-5 h-5' }}
          checked={inBankField.value}
          onChange={inBankField.onChange}
        />
      </ConditionalRenderer>
      <ComponentGuard roles={[UserTypeEnum.SUPER_ADMIN]}>
        <ConditionalRenderer condition={inBank}>
          <div className="flex gap-2 items-center justify-start">
            <input
              type="checkbox"
              checked={isPublicInBankField.value}
              onChange={isPublicInBankField.onChange}
              className="toggle toggle-sm toggle-primary"
            />
            <Text text={t('isPublicInBank')} className="flex" />
          </div>

          <ConditionalRenderer condition={!slug}>
            <ChoiceSelector
              service={unitsQueryKeys.list}
              key={units.map(unit => unit.id).join(',')}
              displayName={unit => unit.name}
              render={({ model }) => <div>{model.name}</div>}
              input={{
                label: t('units.label'),
                placeholder: t('units.placeholder'),
                className: {
                  label: 'text-14 my-1.5',
                },
              }}
              options={{ enabled: inBank }}
              onChange={units =>
                allowedUnitsIdsField.onChange(units.map(unit => unit.id))
              }
              defaultValue={units}
              getKey={unit => unit.id}
            />
          </ConditionalRenderer>
        </ConditionalRenderer>
      </ComponentGuard>
    </Fragment>
  );
}
